import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateGuardrail } from '@/hooks';
import { Edit2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import type { Guardrail } from '@/types';

interface GuardrailCardProps {
  guardrail: Guardrail;
}

export const GuardrailCard = ({ guardrail }: GuardrailCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editDescription, setEditDescription] = useState(guardrail.description);

  const updateGuardrail = useUpdateGuardrail();

  const handleStatusToggle = async (checked: boolean) => {
    try {
      await updateGuardrail.mutateAsync({
        id: guardrail.id,
        data: { status: checked }
      });
      toast.success(`Guardrail ${checked ? 'enabled' : 'disabled'} successfully`);
    } catch {
      toast.error('Failed to update guardrail status');
    }
  };

  const handleEdit = () => {
    setEditDescription(guardrail.description);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editDescription.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      await updateGuardrail.mutateAsync({
        id: guardrail.id,
        data: { description: editDescription.trim() }
      });
      setIsDialogOpen(false);
      toast.success('Guardrail updated successfully');
    } catch {
      toast.error('Failed to update guardrail');
    }
  };

  return (
    <>
      <Card className='gap-3 rounded-2xl'>
        <CardHeader className='p-0 gap-0'>
          <div className='flex items-center justify-between'>
            <CardTitle className='text-sm'>{guardrail.name}</CardTitle>
            <div className='flex items-center gap-1'>
              <Button className='shadow-none border-none' variant='outline' size='icon-sm' onClick={handleEdit}>
                <Edit2 className='h-4 w-4' />
              </Button>
              <div className='h-4 bg-gray-300 border-r mr-2 ml-1.5' />
              <Switch
                checked={guardrail.status}
                onCheckedChange={handleStatusToggle}
                disabled={updateGuardrail.isPending}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-xs text-gray-700 line-clamp-3 min-h-[2lh]'>{guardrail.description}</p>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className='max-w-lg overflow-hidden'>
          <DialogHeader>
            <DialogTitle className='text-sm'>{guardrail.name}</DialogTitle>
          </DialogHeader>
          <Separator className='border-t' />
          <div className='space-y-4'>
            <div>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder='Describe what this guardrail should detect or prevent...'
                className='min-h-[120px] resize-none bg-gray-50 border border-border text-xs md:text-xs'
              />
              <p className='text-xs text-muted-foreground mt-2'>
                This text is used directly in the policy sent to the AI safety checker.
              </p>
            </div>
            <div className='flex gap-2 pt-2 justify-end'>
              <Button
                size='sm'
                className='text-xs font-normal'
                variant='outline'
                onClick={() => setIsDialogOpen(false)}
                disabled={updateGuardrail.isPending}
              >
                Cancel
              </Button>
              <Button
                size='sm'
                className='text-xs font-normal'
                onClick={handleSave}
                disabled={updateGuardrail.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
